import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  QuickFiltersContainer,
  QuickFiltersTitle,
  QuickFilterChip,
} from '../FilterPanel'
import { Icon } from '../../atoms/Icon'
import { ExpandArrow } from '../../atoms/ExpandArrow'
import type { QuickFiltersProps } from './QuickFilters.types'

const MAX_VISIBLE = 3

const ViewMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 3px;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  white-space: nowrap;
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

export const QuickFilters: React.FC<QuickFiltersProps> = ({
  title,
  filters,
  onRemoveFilter,
  className,
}) => {
  const { t } = useTranslation('ui')
  const [expanded, setExpanded] = useState(false)
  const displayTitle = title || t('filters.quickFilters.activeFilters')

  const hasMore = filters.length > MAX_VISIBLE
  const visibleFilters = expanded ? filters : filters.slice(0, MAX_VISIBLE)
  const hiddenCount = filters.length - MAX_VISIBLE

  return (
    <QuickFiltersContainer className={className}>
      <QuickFiltersTitle>{displayTitle}</QuickFiltersTitle>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {visibleFilters.map((filter) => (
          <QuickFilterChip key={filter.id}>
            {filter.label}
            <button
              type="button"
              onClick={() => onRemoveFilter(filter.id)}
              aria-label={t('filters.quickFilters.removeFilter', {
                label: filter.label,
              })}
            >
              <Icon name="x" size="small" />
            </button>
          </QuickFilterChip>
        ))}
        {hasMore && (
          <ViewMoreButton type="button" onClick={() => setExpanded(!expanded)}>
            {expanded
              ? t('filters.quickFilters.viewLess')
              : t('filters.quickFilters.viewMore', { count: hiddenCount })}
            <ExpandArrow $expanded={expanded} direction="down" />
          </ViewMoreButton>
        )}
      </div>
    </QuickFiltersContainer>
  )
}
